import { ReunionMeeting } from '../../services/reunion-streaming.service';

export const BROADCAST_END_GRACE_MS = 20 * 60 * 1000;

export function isCatalogueSelectable(meeting: ReunionMeeting, now: number): boolean {
  if (meeting.catalogueEligible === false) { return false; }
  if (meeting.catalogueValidUntil != null) {
    const validUntil = Date.parse(meeting.catalogueValidUntil);
    if (!Number.isFinite(validUntil) || now >= validUntil) { return false; }
  }
  if (meeting.catalogueAvailableFrom == null) { return true; }
  const availableFrom = Date.parse(meeting.catalogueAvailableFrom);
  return Number.isFinite(availableFrom) && now >= availableFrom;
}

export function isConfirmedLive(meeting: ReunionMeeting): boolean {
  return !!(meeting.race && meeting.race.status === 'live' && meeting.race.estimated === false);
}

export function isMeetingFinished(meeting: ReunionMeeting, now: number): boolean {
  const end = Date.parse(meeting.endsAt || '');
  return meeting.finished === true || meeting.phase === 'ended' || (Number.isFinite(end) && now > end + BROADCAST_END_GRACE_MS);
}

/** Eligibility for the compact rail is presentation only; it never revokes playback. */
export function inBroadcastWindow(meeting: ReunionMeeting, now: number): boolean {
  if (!meeting.playable || !meeting.channelId || isMeetingFinished(meeting, now)) { return false; }
  const start = Date.parse(meeting.startsAt || '');
  const end = Date.parse(meeting.endsAt || '');
  if (Number.isFinite(start) && Number.isFinite(end)) {
    return start <= now && now <= end + BROADCAST_END_GRACE_MS;
  }
  return isConfirmedLive(meeting);
}

function withinScheduledCoverage(meeting: ReunionMeeting, now: number): boolean {
  const start = Date.parse(meeting.startsAt || '');
  const end = Date.parse(meeting.endsAt || '');
  return Number.isFinite(start) && Number.isFinite(end) && start <= now && now <= end;
}

function preferredOwner(meetings: ReunionMeeting[], now: number): ReunionMeeting | undefined {
  return meetings.slice().sort((a, b) => Number(withinScheduledCoverage(b, now)) - Number(withinScheduledCoverage(a, now)) ||
    Number(isConfirmedLive(b)) - Number(isConfirmedLive(a)) ||
    (Date.parse(b.startsAt || '') || 0) - (Date.parse(a.startsAt || '') || 0) || a.id.localeCompare(b.id))[0];
}

export function compactReunions(meetings: ReunionMeeting[], now: number, selectedId: string | null): ReunionMeeting[] {
  const active = meetings.filter((meeting) => inBroadcastWindow(meeting, now));
  const owners = new Set<string>();
  const displayOwners = new Map<string, ReunionMeeting>();
  for (const channelId of Array.from(new Set(active.map((meeting) => meeting.channelId)))) {
    const peers = active.filter((meeting) => meeting.channelId === channelId);
    const reference = peers.find((meeting) => !!meeting.signalOwner);
    let owner: ReunionMeeting | undefined;
    if (reference) {
      owner = meetings.find((meeting) => meeting.id === reference.signalOwner.id && meeting.channelId === channelId);
      const end = owner && Date.parse(owner.endsAt || '');
      // Signal ownership from the guide may lag the local clock. Once the
      // old owner's scheduled coverage ends, a successor whose own coverage
      // has begun takes its place; the old selection remains pinned below.
      if (owner && (owner.finished === true || (Number.isFinite(end) && now > end))) {
        owner = preferredOwner(peers.filter((meeting) => withinScheduledCoverage(meeting, now)), now) ||
          (owner.finished === true ? preferredOwner(peers, now) : undefined) ||
          (peers.some((meeting) => meeting.id === owner.id) ? owner : undefined);
      } else if (!owner || !peers.some((meeting) => meeting.id === owner.id)) {
        // Do not turn a provider fallback to a future owner into a claim that
        // another meeting is currently occupying its picture.
        owner = undefined;
      }
    } else {
      // Pinned rows outside the display window must not compete for ownership.
      owner = preferredOwner(peers, now);
    }
    if (owner) { owners.add(owner.id); displayOwners.set(channelId, owner); }
  }
  return meetings.filter((meeting) => meeting.id === selectedId || owners.has(meeting.id))
    .map((meeting) => {
      const owner = displayOwners.get(meeting.channelId);
      return owner ? { ...meeting, signalOwner: { id: owner.id, label: owner.label } } : meeting;
    })
    .sort((a, b) => (a.reunionNumber || Number.MAX_SAFE_INTEGER) - (b.reunionNumber || Number.MAX_SAFE_INTEGER) ||
      a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
}

export interface CompactMeetingTime {
  key: string;
  minutes?: number;
  time?: string;
}

export function compactMeetingTime(meeting: ReunionMeeting, now: number): CompactMeetingTime {
  if (!meeting.playable) { return { key: 'STREAMING.SHORT_UNAVAILABLE' }; }
  if (isMeetingFinished(meeting, now)) { return { key: 'STREAMING.FINISHED' }; }
  const race = meeting.catalogueRace === undefined ? meeting.race : meeting.catalogueRace;
  if (race && race.status === 'live' && !race.estimated) { return { key: 'STREAMING.LIVE' }; }
  if (race && race.status === 'finished') {
    return { key: race.estimated ? 'STREAMING.COVERAGE' : 'STREAMING.RACE_FINISHED' };
  }
  const start = Date.parse(race && race.startsAt || '');
  if (!Number.isFinite(start)) {
    return { key: inBroadcastWindow(meeting, now) ? 'STREAMING.COVERAGE' : 'STREAMING.SCHEDULE_PENDING' };
  }
  const minutes = Math.ceil((start - now) / 60000);
  if (minutes <= 0) { return { key: 'STREAMING.AT_POST' }; }
  if (minutes < 60) { return { key: 'STREAMING.IN_MINUTES', minutes }; }
  const local = new Date(start);
  return { key: 'STREAMING.AT_TIME', time: String(local.getHours()).padStart(2, '0') + ':' + String(local.getMinutes()).padStart(2, '0') };
}
